import React, { useState, useEffect, useRef } from 'react';
import { Form, Button, Spinner } from 'react-bootstrap';
import { API_ENDPOINTS } from '../../config/constants';
import api from '../../api/api';
import Alert from '../ui/Alert';

/**
 * Email OTP Authentication Form Component
 * Provides email-based OTP verification without Firebase dependency
 */
const EmailOtpForm = ({ 
  onSuccess, 
  userType = 'candidate',
  isSignup = true,
  buttonText = 'Continue'
}) => {
  // Form state
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState('input'); // 'input' or 'verify'
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [failedAttempts, setFailedAttempts] = useState(0);
  
  // Reference to track last request
  const lastRequest = useRef({ timestamp: 0, email: '' });
  
  // Handle countdown timer
  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    }
    
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [countdown]);
  
  // Validate email format
  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };
  
  // Handle sending OTP
  const handleSendOtp = async (e) => {
    e.preventDefault();
    
    // Validate email
    if (!email || !validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }
    
    const now = Date.now();
    
    // Prevent duplicate requests within 2 seconds
    if (now - lastRequest.current.timestamp < 2000 && lastRequest.current.email === email) {
      return;
    }
    
    // Update last request info
    lastRequest.current = { timestamp: now, email };
    
    setLoading(true);
    setError('');
    
    try {
      // Send OTP request to backend using email OTP endpoint
      const response = await api.post(
        API_ENDPOINTS.AUTH.SEND_EMAIL_OTP || `${API_ENDPOINTS.AUTH.SEND_OTP.replace('send-otp', 'send-email-otp')}`,
        { email, userType, isSignup },
        { withCredentials: true } // Enable credentials for session cookies
      );

      // Backend wraps response in { success: true, data: {...} }
      const otpData = response.data?.data || response.data;
      
      // Move to verification step
      setStep('verify');
      setSuccess('Verification code sent to your email');
      setCountdown(30); // 30 seconds countdown for resend
      
    } catch (err) {
      console.error('Error sending OTP:', err);
      
      // Parse backend error responses more thoroughly
      let errorMessage = 'Failed to send verification code. Please try again.';
      
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message === 'Network Error') {
        errorMessage = 'Network error. Please check your internet connection.';
      }
      
      // Check for specific error types
      if (errorMessage.includes('service is not configured')) {
        errorMessage = 'Email service is temporarily unavailable. Please contact support.';
      } else if (errorMessage.includes('too many')) {
        errorMessage = 'Too many attempts. Please wait before trying again.';
      } else if (errorMessage.includes('session')) {
        errorMessage = 'Session expired. Please refresh the page and try again.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle OTP verification
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!otp || otp.length < 6) {
      setError('Please enter a valid verification code');
      return;
    }
    
    if (loading) {
      return; // Prevent double submission
    }
    
    setLoading(true);
    setError('');
    
    try {
      // Verify OTP with backend using email OTP endpoint
      const response = await api.post(
        API_ENDPOINTS.AUTH.VERIFY_EMAIL_OTP || `${API_ENDPOINTS.AUTH.VERIFY_OTP.replace('verify-otp', 'verify-email-otp')}`,
        { email, otpCode: otp },
        { withCredentials: true } // Enable credentials for session cookies
      );

      // Reset failed attempts on success
      setFailedAttempts(0);
      
      // Show success message
      setSuccess('Email verified successfully!');
      
      // Call the success callback with user data and token
      // Wait for auth state to be fully established before the UI updates
      if (onSuccess && typeof onSuccess === 'function') {
        await onSuccess(response.data);
      }
      
    } catch (err) {
      console.error('Error verifying OTP:', err);
      
      // Increment failed attempts counter
      const newFailedAttempts = failedAttempts + 1;
      setFailedAttempts(newFailedAttempts);
      
      // Parse backend error responses more thoroughly
      let errorMessage = 'Failed to verify code. Please try again.';
      let attemptsLeft = null;
      
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message === 'Network Error') {
        errorMessage = 'Network error. Please check your internet connection.';
      }
      
      // Extract attempts remaining from API response if available
      if (err.response?.data?.attemptsRemaining !== undefined) {
        attemptsLeft = err.response.data.attemptsRemaining;
      }
      
      // Check for specific error types
      if (errorMessage.includes('no verification code found')) {
        errorMessage = 'Your session expired. Please request a new code.';
      } else if (errorMessage.includes('expired')) {
        errorMessage = 'Verification code expired. Please request a new one.';
      } else if (errorMessage.includes('invalid')) {
        // Add remaining attempts info if available
        if (attemptsLeft !== null) {
          errorMessage = `Incorrect code. Please check and try again. (${attemptsLeft} attempts remaining)`;
        } else {
          const calculatedAttemptsLeft = Math.max(0, 3 - newFailedAttempts);
          errorMessage = `Incorrect code. Please check and try again. (${calculatedAttemptsLeft} attempts remaining)`;
        }
      } else if (errorMessage.includes('too many failed attempts')) {
        errorMessage = 'Too many failed attempts. Please request a new code.';
      }
      
      setError(errorMessage);
      
      // After 3 failed attempts, reset to email input step
      if (newFailedAttempts >= 3) {
        setTimeout(() => {
          setStep('input');
          setOtp('');
          setFailedAttempts(0);
          setError('Too many failed attempts. Please request a new code.');
        }, 2000);
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Handle resending OTP
  const handleResendOtp = async () => {
    if (countdown > 0) return;
    
    // Reset failed attempts when requesting new code
    setFailedAttempts(0);
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      // Resend OTP request using email OTP endpoint
      const response = await api.post(
        API_ENDPOINTS.AUTH.SEND_EMAIL_OTP || `${API_ENDPOINTS.AUTH.SEND_OTP.replace('send-otp', 'send-email-otp')}`,
        { email, userType, isSignup },
        { withCredentials: true } // Enable credentials for session cookies
      );

      // Get OTP data
      const otpData = response.data?.data || response.data;
      
      // Success - restart countdown
      setCountdown(30);
      setSuccess('New verification code sent to your email');
      
    } catch (err) {
      console.error('Error resending OTP:', err);
      setError(err.response?.data?.message || 'Failed to resend verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle back button
  const handleBack = () => {
    setStep('input');
    setError('');
    setSuccess('');
    setOtp('');
  };
  
  // Render email input form
  if (step === 'input') {
    return (
      <form onSubmit={handleSendOtp} className="space-y-4">
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}
        
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
            disabled={loading}
            required
            className="w-full p-3 bg-white border-b-2 border-black focus:border-yellow-400 focus:outline-none transition-all"
          />
          <p className="text-xs text-gray-500 mt-1">
            We'll send a verification code to this email.
          </p>
        </div>
        
        <button 
          type="submit"
          disabled={loading}
          className="w-full font-bold bg-black text-white px-6 py-3 border-2 border-black hover:bg-gray-900 transition-colors disabled:bg-gray-400 disabled:border-gray-400"
        >
          {loading ? (
            <span className="flex items-center justify-center">
              <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
              Sending...
            </span>
          ) : (
            'Get Verification Code'
          )}
        </button>
      </form>
    );
  }
  
  // Render OTP verification form
  return (
    <form onSubmit={handleVerifyOtp} className="space-y-4">
      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}
      
      <p className="text-center mb-4 text-sm text-gray-600">
        We've sent a verification code to <strong className="text-black">{email}</strong>
      </p>
      
      <div>
        <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-1">Verification Code</label>
        <input
          id="otp"
          type="text"
          value={otp}
          onChange={(e) => setOtp(e.target.value)}
          placeholder="Enter 6-digit code"
          disabled={loading}
          maxLength={6}
          required
          className="w-full p-3 bg-white border-b-2 border-black focus:border-yellow-400 focus:outline-none transition-all"
        />
      </div>
      
      <div className="flex justify-between items-center mb-3">
        <button 
          type="button"
          onClick={handleBack} 
          disabled={loading}
          className="text-sm text-black hover:text-yellow-600 underline disabled:text-gray-400"
        >
          Change Email
        </button>
        
        <button 
          type="button"
          onClick={handleResendOtp} 
          disabled={loading || countdown > 0}
          className="text-sm text-black hover:text-yellow-600 underline disabled:text-gray-400"
        >
          {countdown > 0 ? `Resend in ${countdown}s` : 'Resend Code'}
        </button>
      </div>
      
      <button 
        type="submit"
        disabled={loading}
        className="w-full font-bold bg-black text-white px-6 py-3 border-2 border-black hover:bg-gray-900 transition-colors disabled:bg-gray-400 disabled:border-gray-400"
      >
        {loading ? (
          <span className="flex items-center justify-center">
            <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
            Verifying...
          </span>
        ) : (
          buttonText
        )}
      </button>
    </form>
  );
};

export default EmailOtpForm;
